import { getHost } from '@repo/host/host';
import type { UserAccount } from '@repo/types/account';
import type { AnalyticEvents, ValidPropertyType } from '@repo/types/analytic-events/analytic-events';
import { HostType } from '@repo/types/host';
import { createLogger } from '@repo/utility/logging';
import { Amplitude } from './amplitude';
import { GoogleAnalytics } from './google-analytics';

export type { ValidPropertyType };

export interface EventDispatch {
    readonly ga: boolean;
    readonly amplitude: boolean;
}

export interface Screen {
    readonly name: string;
    readonly class: string;
    readonly params?: object;
}

export const EVENT_DISPATCH_ALL: EventDispatch = Object.freeze({
    ga: true,
    amplitude: true,
});

export const EVENT_DISPATCH_AMPLITUDE: EventDispatch = Object.freeze({
    ga: false,
    amplitude: true,
});

export const EVENT_DISPATCH_GA: EventDispatch = Object.freeze({
    ga: true,
    amplitude: false,
});

export type AnalyticProvider = {
    initialize: () => void;
    track<T extends keyof AnalyticEvents>(event: T, params: AnalyticEvents[T]['params']): void;
    screenView(screen: Screen): void;
    setUserProperty(name: string, value: ValidPropertyType): void;
};

export type AnalyticsConfig = {
    account: UserAccount | null;
    userId?: string;
    enabled: boolean;
    amplitudeApiKey: string;
    gtmId: string;
    platform: string;
    hostType?: HostType;
    disableGA?: boolean;
};

const logger = createLogger({
    color: 'purple',
    prefix: 'Analytics',
    environment: process.env.NODE_ENV ?? 'production',
});

const MAX_QUEUED_EVENTS = 100;

export class Analytics {
    readonly #ga: GoogleAnalytics;
    readonly #amplitude: Amplitude;
    readonly #hostType: HostType;
    #disableGA: boolean;
    #initialized = false;
    #gaInitialized = false;
    #queued: (() => void)[] = [];

    public constructor({
        account,
        userId,
        enabled,
        amplitudeApiKey,
        gtmId,
        platform,
        hostType = HostType.WEB,
        disableGA = false,
    }: AnalyticsConfig) {
        this.#ga = new GoogleAnalytics(gtmId);
        this.#amplitude = new Amplitude(amplitudeApiKey, account?.uuid ?? userId ?? null, enabled, platform);
        this.#hostType = hostType;
        this.#disableGA = disableGA;
    }

    public initialize(): void {
        if (this.#initialized) {
            return;
        }

        if (process.env.NODE_ENV === 'development') {
            this.#initialized = true;
            this.#drainQueue();
            return;
        }

        if (this.#hostType !== HostType.WEB) {
            this.#initialized = true;
            this.#drainQueue();
            return;
        }

        if (!this.#disableGA) {
            this.#ga.initialize();
            this.#gaInitialized = true;
        }
        this.#amplitude.initialize();
        this.#initialized = true;
        this.#drainQueue();
    }

    // Mutates config in place so callers don't need to construct a new Analytics
    // instance when embed/consent state resolves. Avoids stale closures captured by
    // useEffect([]) call sites (e.g. the /download auto-start event).
    public reconfigure({ disableGA, userId }: { disableGA?: boolean; userId?: string | null }): void {
        if (userId !== undefined) {
            this.#amplitude.setUserId(userId);
        }

        if (disableGA === undefined) {
            return;
        }
        const wasDisabled = this.#disableGA;
        this.#disableGA = disableGA;

        if (wasDisabled && !disableGA && this.#initialized && !this.#gaInitialized && this.#hostType === HostType.WEB) {
            if (process.env.NODE_ENV !== 'development') {
                this.#ga.initialize();
                this.#gaInitialized = true;
            }
        }
    }

    #drainQueue(): void {
        const queue = this.#queued;
        this.#queued = [];
        queue.forEach(replay => {
            try {
                replay();
            } catch (error) {
                logger.warn('Queued analytics event replay failed', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        });
    }

    #enqueue(replay: () => void): void {
        if (this.#queued.length >= MAX_QUEUED_EVENTS) {
            this.#queued.shift();
        }
        this.#queued.push(replay);
    }

    public setUserProperty(name: string, value: ValidPropertyType, dispatch: EventDispatch = EVENT_DISPATCH_ALL): void {
        if (process.env.NODE_ENV === 'development') {
            logger.info(`setUserProperty: ${name} = ${value}`);

            return;
        }
        if (this.#hostType !== HostType.WEB) {
            return;
        }
        if (!this.#initialized) {
            this.#enqueue(() => this.setUserProperty(name, value, dispatch));
            return;
        }

        if (dispatch.ga && !this.#disableGA) {
            this.#ga.setUserProperty(name, value);
        }

        if (dispatch.amplitude) {
            this.#amplitude.setUserProperty(name, value);
        }
    }

    public event<T extends keyof AnalyticEvents>(
        name: T,
        params: AnalyticEvents[T]['params'],
        dispatch: EventDispatch = EVENT_DISPATCH_ALL,
    ): void {
        if (process.env.NODE_ENV === 'development') {
            logger.info(`event: ${name}`, { params, dispatch });
            return;
        }

        // Route to host.event() if not web
        if (this.#hostType !== HostType.WEB) {
            try {
                const host = getHost();
                void host.event(name, params, dispatch);
            } catch (error) {
                console.warn('Failed to route event to host:', error);
            }
            return;
        }
        if (!this.#initialized) {
            this.#enqueue(() => this.event(name, params, dispatch));
            return;
        }

        if (dispatch.ga && !this.#disableGA) {
            this.#ga.track(name, params);
        }

        if (dispatch.amplitude) {
            this.#amplitude.track(name, params);
        }
    }

    public screenView(screen: Screen, dispatch: EventDispatch = EVENT_DISPATCH_ALL): void {
        if (process.env.NODE_ENV === 'development') {
            logger.info(`screenView: ${screen.name}`, { class: screen.class, params: screen.params, dispatch });

            return;
        }
        if (this.#hostType !== HostType.WEB) {
            return;
        }
        if (!this.#initialized) {
            this.#enqueue(() => this.screenView(screen, dispatch));
            return;
        }

        if (dispatch.ga && !this.#disableGA) {
            this.#ga.screenView(screen);
        }

        if (dispatch.amplitude) {
            this.#amplitude.screenView(screen);
        }
    }
}
